import { useEffect, useRef } from 'react';
import styles from './ConfirmDialog.module.css';

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  /** Body text. `\n\n` is preserved as paragraph spacing (white-space: pre-line). */
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm action as destructive (red) and focus Cancel by default. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Styled replacement for `window.confirm()`. The native dialog can't be themed,
 * blocks the event loop, and wedges browser automation; this matches the app's
 * modal styling (see NewUploadDialog), traps initial focus, and closes on
 * Escape.
 *
 * For a destructive action, initial focus lands on Cancel so a stray Enter does
 * not trigger the deletion.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Escape cancels.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onCancel]);

  // Focus the safer control (Cancel for destructive, otherwise Confirm) on open.
  useEffect(() => {
    if (!open) return;
    const target = destructive ? cancelRef.current : confirmRef.current;
    const timer = setTimeout(() => target?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open, destructive]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Confirm'}
    >
      <div className={styles.dialog}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          <button
            type="button"
            ref={cancelRef}
            className={styles.cancelButton}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`${styles.confirmButton} ${destructive ? styles.confirmDestructive : ''}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
